import { routeChatIdFromQuery } from '@/app-shell/layout/Navigation/hooks/routeChatIdFromQuery';
import { filterChatsForWorkAvailability } from '@heidi/agents/contracts';
import { useCanUseWorkHome } from '@heidi/agents/data';
import { Button } from '@heidi/design-system/button';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@heidi/design-system/collapsible';
import { SidebarGroup, SidebarMenu } from '@heidi/design-system/page-shell';
import { focusRing } from '@heidi/design-system/styles';
import { TypographyCaption, typographyVariant } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { ChevronDown } from 'lucide-react';
import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { navV3Messages } from '../nav-v3.messages';
import { RecentChatRow } from '../recent-chats/recent-chat-row';
import { useNavSavedChats } from '../saved-chats-context';

/** Collapsed list length — keeps Pinned from crowding Recents. */
const INITIAL_VISIBLE_PINNED = 5;

/**
 * "Pinned" tray section — the nav-v3 counterpart to Chat's
 * `SavedChatsSidebarSection`, backed by the same saved-chats endpoint and
 * hooks (`useSavedChats`), just relabelled Pin/Unpin/Pinned per the terminology
 * decision in the spec. Renders above `RecentsSection`, newest first (server
 * order, unchanged), and stays hidden while the saved-chats query is pending
 * or once filtered down to nothing — never an empty labelled group.
 *
 * Reads `useSavedChats()` through `useNavSavedChats()` — the shared context
 * mounted once by `SavedChatsProvider` in `nav-v3.tsx` — rather than calling
 * the hook itself, so this section and every row's `ChatRowActionsMenu` share
 * one set of query/mutation subscriptions instead of one each.
 *
 * Heading, collapse chevron and show-more/show-less behaviour mirror the
 * Scheduled section (`WorkflowsSidebarCollapsibleSection`'s Agent Home
 * presentation and `RoutinesShowMoreButton`).
 */
export const PinnedChatsSection = () => {
  const intl = useIntl();
  const router = useRouter();
  const { savedChats, savedChatsPending } = useNavSavedChats();
  const isWorkEnabled = useCanUseWorkHome();
  const [sectionOpen, setSectionOpen] = useState(true);
  const [showAll, setShowAll] = useState(false);

  // Same derivation as `RecentsSection`, so a pinned chat that is also the
  // one currently open is identified the same way everywhere in the nav.
  const currentChatId = useMemo(() => {
    if (!router.pathname.includes('/scribe/chat')) return null;
    return routeChatIdFromQuery(router.query.id);
  }, [router.pathname, router.query.id]);

  const pinnedChats = useMemo(
    () => filterChatsForWorkAvailability(savedChats, isWorkEnabled),
    [savedChats, isWorkEnabled]
  );

  // Mirrors `WorkflowsSidebarSection`'s `visibleWorkflows`: slice the head,
  // then keep the open chat visible even if it sits past the collapse cut —
  // otherwise pinning 6+ chats and opening the oldest makes it vanish from
  // the nav entirely.
  const visiblePinnedChats = useMemo(() => {
    if (showAll || pinnedChats.length <= INITIAL_VISIBLE_PINNED) {
      return pinnedChats;
    }
    const head = pinnedChats.slice(0, INITIAL_VISIBLE_PINNED);
    if (currentChatId && !head.some((chat) => chat.id === currentChatId)) {
      const focused = pinnedChats.find((chat) => chat.id === currentChatId);
      if (focused) return [...head, focused];
    }
    return head;
  }, [currentChatId, showAll, pinnedChats]);

  const canToggle = pinnedChats.length > INITIAL_VISIBLE_PINNED;
  // Derived from what is actually rendered, not `length - INITIAL_VISIBLE_PINNED`
  // — the focused-chat exception above can make `visiblePinnedChats` six long
  // while still collapsed, which `pinnedShowMore`'s `=0` plural branch renders as.
  const hiddenCount = Math.max(0, pinnedChats.length - visiblePinnedChats.length);

  if (savedChatsPending || pinnedChats.length === 0) {
    return null;
  }

  return (
    <SidebarGroup data-testid="sidebar-pinned-section">
      <Collapsible open={sectionOpen} onOpenChange={setSectionOpen} className="w-full">
        {/* Same row as Scheduled (WorkflowsSidebarCollapsibleSection's Agent
            Home branch) and the Recents date-group headers: the whole h-8
            px-2 row toggles, and its chevron appears on hover / focus-visible
            (always shown where the pointer cannot hover). */}
        <CollapsibleTrigger asChild>
          <button
            type="button"
            data-testid="sidebar-pinned-section-toggle"
            className={cn(
              'group/pinned-heading flex h-8 w-full items-center gap-1 overflow-hidden whitespace-nowrap rounded-md px-2',
              'text-text-secondary',
              focusRing,
              'transition-colors duration-200 ease-out motion-reduce:transition-none',
              'hover:text-text-primary'
            )}
          >
            <span
              className={cn(
                typographyVariant({ variant: 'p2' }),
                'min-w-0 flex-1 truncate text-start'
              )}
              data-testid="sidebar-pinned-heading"
            >
              {intl.formatMessage(navV3Messages.pinned)}
            </span>
            {/* size-7 slot matches the Recents header's 28px icon buttons so both share one right edge. */}
            <span
              aria-hidden="true"
              className="flex size-7 shrink-0 items-center justify-center"
            >
              <ChevronDown
                data-testid="sidebar-pinned-section-chevron"
                className={cn(
                  'size-4 text-text-secondary pointer-fine:sm:opacity-0',
                  'transition-[opacity,transform] duration-200 ease-out motion-reduce:transition-none',
                  'group-hover/pinned-heading:opacity-100 group-focus-visible/pinned-heading:opacity-100',
                  !sectionOpen && '-rotate-90 rtl:rotate-90'
                )}
              />
            </span>
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent className="overflow-hidden will-change-[height,opacity,transform] motion-safe:data-[state=closed]:animate-collapsible-up motion-safe:data-[state=open]:animate-collapsible-down">
          <SidebarMenu>
            {visiblePinnedChats.map((chat) => (
              <RecentChatRow key={chat.id} chat={chat} isPinnedSection />
            ))}
          </SidebarMenu>
          {canToggle ? (
            <Button
              type="button"
              variant="link"
              size="xs"
              disableAutomaticTypographyClasses
              onClick={() => setShowAll((prev) => !prev)}
              className="flex h-8 w-full items-center justify-start rounded-[10px] px-3 py-1 text-start text-text-secondary hover:bg-surface-accent-hover hover:text-text-primary"
              data-testid="sidebar-pinned-show-more"
            >
              <TypographyCaption>
                {showAll
                  ? intl.formatMessage(navV3Messages.pinnedShowLess)
                  : intl.formatMessage(navV3Messages.pinnedShowMore, {
                      count: hiddenCount,
                    })}
              </TypographyCaption>
            </Button>
          ) : null}
        </CollapsibleContent>
      </Collapsible>
    </SidebarGroup>
  );
};
